/**
 * Legal pages — Mentions légales, Politique de confidentialité, CGV.
 * Content sourced from sellerie-academie.fr (Aug 2025).
 */
import { Link, useParams } from 'react-router-dom';
import { ChevronRight, FileText } from 'lucide-react';
import { SUPPORTED_LANGS, DEFAULT_LANG } from '../i18n/config';

const usePrefix = () => {
  const { lang } = useParams();
  const l = SUPPORTED_LANGS.includes(lang) ? lang : DEFAULT_LANG;
  return `/${l}`;
};

// ─────────────────────────────────────────── Shared layout
const LegalShell = ({ kicker, title, lastUpdate, children }) => {
  const prefix = usePrefix();
  return (
    <div className="bg-[#FAF7F0] min-h-screen">
      {/* Hero */}
      <section className="bg-[#1F2A4A] text-white pt-32 pb-16 md:pt-40 md:pb-24 relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-[0.05] pointer-events-none mix-blend-overlay"
          style={{
            backgroundImage:
              "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")",
          }}
        />
        <div className="absolute top-0 right-0 w-1/2 h-full opacity-15 pointer-events-none" style={{ backgroundImage: 'radial-gradient(circle at 80% 50%, #C9B896 0%, transparent 60%)' }} />

        <div className="relative max-w-4xl mx-auto px-6 md:px-12">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-xs font-mono uppercase tracking-[0.25em] text-[#C9B896]/80 mb-8">
            <Link to={prefix} className="hover:text-[#C9B896] transition-colors">Accueil</Link>
            <ChevronRight className="w-3 h-3" />
            <span className="text-white/60">Mentions légales</span>
          </nav>
          <p className="text-xs font-mono uppercase tracking-[0.3em] text-[#C9B896] mb-4 flex items-center gap-2">
            <FileText className="w-3 h-3" /> {kicker}
          </p>
          <h1 className="font-light text-3xl md:text-5xl tracking-tight text-white leading-[1.05]">
            {title}
          </h1>
          {lastUpdate && (
            <p className="mt-6 text-xs font-mono uppercase tracking-[0.25em] text-[#C9B896]/80">
              · Mis à jour le {lastUpdate}
            </p>
          )}
        </div>
      </section>

      {/* Content */}
      <section className="py-10 md:py-14">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <article className="bg-white border border-[#1F2A4A]/10 rounded-3xl p-8 md:p-14 shadow-[0_30px_80px_-40px_rgba(31,42,74,0.18)]">
            <div className="prose-legal">
              {children}
            </div>
          </article>

          {/* Cross-links */}
          <div className="grid sm:grid-cols-3 gap-3 mt-8">
            <Link to={`${prefix}/mentions-legales`} className="group bg-white border border-[#1F2A4A]/10 hover:border-[#C9B896] rounded-2xl px-5 py-4 flex items-center justify-between transition-colors">
              <span className="text-xs font-mono uppercase tracking-[0.25em] text-[#1F2A4A]">Mentions légales</span>
              <ChevronRight className="w-4 h-4 text-[#C9B896] opacity-50 group-hover:opacity-100" />
            </Link>
            <Link to={`${prefix}/confidentialite`} className="group bg-white border border-[#1F2A4A]/10 hover:border-[#C9B896] rounded-2xl px-5 py-4 flex items-center justify-between transition-colors">
              <span className="text-xs font-mono uppercase tracking-[0.25em] text-[#1F2A4A]">Confidentialité</span>
              <ChevronRight className="w-4 h-4 text-[#C9B896] opacity-50 group-hover:opacity-100" />
            </Link>
            <Link to={`${prefix}/cgv`} className="group bg-white border border-[#1F2A4A]/10 hover:border-[#C9B896] rounded-2xl px-5 py-4 flex items-center justify-between transition-colors">
              <span className="text-xs font-mono uppercase tracking-[0.25em] text-[#1F2A4A]">CGV</span>
              <ChevronRight className="w-4 h-4 text-[#C9B896] opacity-50 group-hover:opacity-100" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

// Premium typography styles, injected once
const LegalStyles = () => (
  <style>{`
    .prose-legal h2 {
      font-family: 'Cormorant Garamond', Georgia, serif;
      font-weight: 400;
      font-size: 1.75rem;
      color: #1F2A4A;
      margin: 2.25rem 0 1rem;
      letter-spacing: -0.01em;
    }
    .prose-legal h3 {
      font-family: 'Cormorant Garamond', Georgia, serif;
      font-weight: 500;
      font-size: 1.25rem;
      color: #1F2A4A;
      margin: 1.5rem 0 0.5rem;
    }
    .prose-legal p { color: #4A5568; line-height: 1.85; margin-bottom: 1rem; font-size: 15px; }
    .prose-legal strong { color: #1F2A4A; font-weight: 600; }
    .prose-legal ul { margin: 0.5rem 0 1.25rem 1.25rem; }
    .prose-legal li { color: #4A5568; line-height: 1.85; margin-bottom: 0.4rem; font-size: 15px; list-style: none; position: relative; padding-left: 1.5rem; }
    .prose-legal li::before { content: "·"; color: #C9B896; position: absolute; left: 0; font-size: 22px; line-height: 1; top: 4px; }
    .prose-legal a { color: #C9B896; text-decoration: none; border-bottom: 1px solid #C9B89640; }
    .prose-legal a:hover { color: #1F2A4A; border-bottom-color: #1F2A4A; }
    .prose-legal hr { margin: 2.25rem 0; border: 0; border-top: 1px solid #1F2A4A1A; }
    .prose-legal .kicker {
      font-family: 'JetBrains Mono', 'Courier New', monospace;
      font-size: 10px;
      letter-spacing: 0.3em;
      text-transform: uppercase;
      color: #C9B896;
      margin: 1.5rem 0 0.5rem;
    }
  `}</style>
);

// ─────────────────────────────────────────── 1. Mentions légales
export const MentionsLegalesPage = () => (
  <>
    <LegalStyles />
    <LegalShell kicker="MENTIONS LÉGALES" title="Mentions légales" lastUpdate="01 janvier 2026">
      <p className="kicker">Éditeur du site</p>
      <p>
        <strong>Sellerie Académie</strong> — Centre de formation en Sellerie et Garnissage.<br />
        Atelier : 25 Avenue du Tubé · ZAC du Tubé Sud · Pépinière d'entreprises, 13800 Istres, France.<br />
        SIRET : 492 866 462 00053<br />
        NDA : 93132488413 (enregistré auprès du Préfet de Région PACA — ne vaut pas agrément de l'État)<br />
        Téléphone : <a href="tel:+33465011066">04 65 01 10 66</a><br />
        Email : <a href="mailto:contact@sellerie-academie.fr">contact@sellerie-academie.fr</a>
      </p>

      <p className="kicker">Directeur de la publication</p>
      <p>Benoît Kristof</p>

      <p className="kicker">Webmaster</p>
      <p>
        Agence MSC<br />
        Email : <a href="mailto:contact@agence-msc.fr">contact@agence-msc.fr</a>
      </p>

      <p className="kicker">Hébergement du site</p>
      <p>
        PlanetHoster<br />
        4416 rue Louis-B.-Mayer, Laval, Québec, H7P 0G1, Canada
      </p>

      <h2>Propriété intellectuelle</h2>
      <p>
        Le site <strong>www.sellerie-academie.fr</strong> ainsi que l'ensemble de son contenu (textes, images, vidéos, logos, identité graphique) sont protégés par les droits de propriété intellectuelle.
        Toute reproduction, distribution, modification ou utilisation non autorisée de ces éléments est interdite
        sans l'accord préalable et écrit de Sellerie Académie.
      </p>

      <h2>Responsabilité</h2>
      <p>
        Sellerie Académie met tout en œuvre pour assurer l'exactitude des informations diffusées sur ce site.
        Elle ne pourra cependant être tenue responsable des erreurs ou omissions présentes, ni des dommages
        directs ou indirects pouvant résulter de l'accès ou de l'utilisation du site.
      </p>

      <h2>Protection des données personnelles</h2>
      <p>
        Conformément à la loi Informatique et Libertés du 6 janvier 1978 modifiée et au Règlement général sur la
        protection des données (RGPD), vous disposez d'un droit d'accès, de rectification, de suppression et
        d'opposition concernant vos données personnelles. Pour exercer ces droits, contactez-nous à l'adresse
        <a href="mailto:contact@sellerie-academie.fr"> contact@sellerie-academie.fr</a>.
      </p>

      <h2>Certification Qualiopi</h2>
      <p>
        Sellerie Académie est certifiée <strong>Qualiopi</strong> au titre de la catégorie d'action
        <strong> « Actions de formation »</strong>.
      </p>
      <ul>
        <li><strong>Numéro de certification :</strong> QUA23050043</li>
        <li><strong>Organisme certificateur :</strong> ACTIVCERT (accrédité Cofrac)</li>
        <li><strong>Date de première émission :</strong> 11 juillet 2023</li>
        <li><strong>Validité :</strong> jusqu'au 10 juillet 2026</li>
        <li><strong>NDA :</strong> 93132488413</li>
      </ul>
      <p>
        <a href="/qualiopi/certificat-qualiopi-sellerie-academie.pdf" target="_blank" rel="noreferrer">
          Télécharger le certificat Qualiopi (PDF)
        </a>
      </p>

      <h2>Cookies</h2>
      <p>
        Le site utilise des cookies fonctionnels pour améliorer l'expérience utilisateur. Aucun cookie publicitaire
        ou de traçage tiers n'est déposé sans votre consentement. Vous pouvez configurer votre navigateur pour
        refuser les cookies à tout moment.
      </p>
    </LegalShell>
  </>
);

// ─────────────────────────────────────────── 2. Politique de confidentialité
export const ConfidentialitePage = () => (
  <>
    <LegalStyles />
    <LegalShell kicker="RGPD · DONNÉES PERSONNELLES" title="Politique de confidentialité" lastUpdate="01 janvier 2026">
      <p>
        La protection de vos données personnelles est importante pour <strong>Sellerie Académie</strong>. Cette
        politique de confidentialité explique comment nous collectons, utilisons et protégeons les informations
        que vous nous fournissez via notre site internet <strong>www.sellerie-academie.fr</strong>.
      </p>

      <h2>1. Collecte des données personnelles</h2>
      <p>
        Dans le cadre de l'étude de projets de formation, nous collectons des informations personnelles via les
        formulaires présents sur notre site (formulaire de téléchargement de programme, candidature, contact,
        newsletter). Ces informations peuvent inclure, sans s'y limiter, votre <strong>nom, prénom, adresse
        email, numéro de téléphone</strong> et tout autre renseignement nécessaire à l'analyse de votre projet.
      </p>

      <h2>2. Utilisation des données personnelles</h2>
      <p>
        Les données que vous nous transmettez sont utilisées uniquement pour réaliser l'étude de votre projet de
        formation. Elles nous permettent de vous contacter, de mieux comprendre vos besoins et de vous fournir
        un accompagnement personnalisé. Elles ne sont jamais utilisées à des fins de prospection commerciale tierce.
      </p>

      <h2>3. Durée de conservation des données</h2>
      <p>
        Les données personnelles collectées sont conservées pendant une durée maximale de <strong>12 mois</strong>,
        sauf demande de suppression effectuée par le propriétaire des données. Les données relatives aux candidats
        et élèves admis sont conservées plus longtemps pour répondre aux obligations légales liées à l'activité
        de formation professionnelle (Qualiopi, OPCO, comptabilité).
      </p>

      <h2>4. Droit d'accès, de rectification et de suppression</h2>
      <p>
        Conformément à la législation en vigueur (Règlement général sur la protection des données — RGPD), vous
        disposez des droits suivants concernant vos données personnelles :
      </p>
      <ul>
        <li>Le droit d'<strong>accès</strong> à vos données.</li>
        <li>Le droit de <strong>rectification</strong> de vos données si elles sont inexactes ou incomplètes.</li>
        <li>Le droit de <strong>suppression</strong> de vos données, sous réserve de respecter les conditions légales de conservation.</li>
        <li>Le droit à la <strong>portabilité</strong> de vos données.</li>
        <li>Le droit d'<strong>opposition</strong> et de limitation du traitement.</li>
      </ul>
      <p>
        Pour exercer ces droits, envoyez une demande par email à :
        <a href="mailto:sellerieacademie13@gmail.com"> sellerieacademie13@gmail.com</a>.
        Nous répondrons sous un délai maximum d'un mois.
      </p>

      <h2>5. Sécurité des données</h2>
      <p>
        Nous mettons en œuvre des mesures de sécurité techniques et organisationnelles appropriées pour protéger
        vos données personnelles contre toute forme de divulgation, de perte ou d'accès non autorisé (chiffrement
        TLS, accès restreints, sauvegardes régulières, mots de passe chiffrés).
      </p>

      <h2>6. Cookies</h2>
      <p>
        Le site utilise des cookies pour améliorer l'expérience utilisateur (session, préférence de langue). Ces
        cookies ne collectent aucune information permettant d'identifier personnellement un utilisateur. Vous
        pouvez désactiver les cookies via les paramètres de votre navigateur.
      </p>

      <h2>7. Partage des données</h2>
      <p>
        Nous ne partageons pas vos informations personnelles avec des tiers sans votre consentement, sauf dans les
        cas où cela est exigé par la loi (administration fiscale, organismes de financement de la formation
        professionnelle — OPCO, Pôle Emploi, dans le strict cadre de votre dossier).
      </p>

      <h2>8. Modifications de la politique de confidentialité</h2>
      <p>
        Nous nous réservons le droit de modifier cette politique de confidentialité à tout moment. Toute
        modification substantielle sera publiée sur cette page avec une nouvelle date de mise à jour.
      </p>

      <h2>9. Contact</h2>
      <p>
        Pour toute question relative à la présente politique ou au traitement de vos données personnelles :<br />
        <strong>Sellerie Académie</strong> — 1 Boulevard des Jardins, 13800 Istres, France.<br />
        Email : <a href="mailto:sellerieacademie13@gmail.com">sellerieacademie13@gmail.com</a>
      </p>
    </LegalShell>
  </>
);

// ─────────────────────────────────────────── 3. CGV
export const CGVPage = () => {
  const prefix = usePrefix();
  return (
  <>
    <LegalStyles />
    <LegalShell kicker="CONDITIONS GÉNÉRALES" title="Conditions générales de vente" lastUpdate="01 janvier 2026">
      <p>
        Les présentes conditions générales de vente s'appliquent à toutes les prestations de formation
        professionnelle conclues par <strong>Sellerie Académie</strong>, organisme de formation certifié Qualiopi
        sis 1 Boulevard des Jardins, 13800 Istres — SIRET 492 866 462 00053.
      </p>

      <h2>Article 1 — Objet</h2>
      <p>
        Les présentes CGV ont pour objet de définir les conditions dans lesquelles Sellerie Académie réalise les
        prestations de formation au profit de ses stagiaires (particuliers, salariés, demandeurs d'emploi,
        professionnels) dans le domaine de la sellerie-garnissage.
      </p>

      <h2>Article 2 — Inscription et formation des contrats</h2>
      <p>
        L'inscription à une session de formation est validée à réception du dossier de candidature complet
        (formulaire en ligne, pièces justificatives) et de la signature du contrat ou de la convention de
        formation, accompagnée du règlement de l'acompte demandé. À défaut, Sellerie Académie se réserve le droit
        d'annuler l'inscription.
      </p>

      <h2>Article 3 — Tarifs et modalités de paiement</h2>
      <p>
        Les tarifs des formations sont indiqués en euros TTC sur le site et sur chaque programme remis au stagiaire.
        Ils incluent l'ensemble des frais pédagogiques. Le paiement peut être effectué :
      </p>
      <ul>
        <li>Par virement bancaire ou carte bancaire en ligne sécurisée (Stripe).</li>
        <li>Par un financeur tiers : France Travail, FAFCEA, OPCO, employeur — dans le cadre d'une convention.</li>
        <li>En plusieurs échéances pour les formations longues, selon les modalités prévues au contrat.</li>
      </ul>

      <h2>Article 4 — Droit de rétractation</h2>
      <p>
        Conformément à l'article L.221-18 du Code de la consommation, le stagiaire particulier dispose d'un
        <strong> délai de rétractation de 14 jours</strong> à compter de la signature du contrat de formation, sans
        avoir à justifier de motif ni à payer de pénalité. La rétractation s'exerce par lettre recommandée ou email
        à <a href="mailto:contact@sellerie-academie.fr">contact@sellerie-academie.fr</a>. Au-delà, voir l'article 5.
      </p>

      <h2>Article 5 — Annulation et report</h2>
      <h3>Annulation à l'initiative du stagiaire</h3>
      <p>
        Toute annulation doit être notifiée par écrit. Les frais d'annulation sont les suivants :
      </p>
      <ul>
        <li>Plus de 30 jours avant le début de la formation : remboursement intégral des sommes versées (hors frais de dossier de 50 € TTC).</li>
        <li>Entre 30 et 15 jours : 50 % du montant total reste dû.</li>
        <li>Moins de 15 jours ou abandon en cours de formation : la totalité du prix reste due, sauf cas de force majeure dûment justifié.</li>
      </ul>
      <h3>Annulation à l'initiative de Sellerie Académie</h3>
      <p>
        Sellerie Académie se réserve la possibilité d'annuler ou de reporter une session si le nombre minimum de
        participants n'est pas atteint, ou en cas d'événement de force majeure. Dans ce cas, les stagiaires sont
        informés au plus tôt et bénéficient d'un report sans frais ou d'un remboursement intégral.
      </p>

      <h2>Article 6 — Réalisation des prestations</h2>
      <p>
        Sellerie Académie s'engage à dispenser ses formations avec professionnalisme, en mettant à disposition
        les moyens humains, pédagogiques et matériels adaptés. Les locaux et équipements sont conformes aux
        normes de sécurité et accessibilité. Une attestation de fin de formation est remise à chaque stagiaire
        ayant suivi l'intégralité de la session.
      </p>

      <h2>Article 7 — Propriété intellectuelle</h2>
      <p>
        L'ensemble des supports pédagogiques (manuels, vidéos, fiches techniques, patrons) reste la propriété
        exclusive de Sellerie Académie. Toute reproduction, diffusion ou utilisation commerciale en dehors du
        cadre strictement personnel du stagiaire est interdite sans autorisation écrite préalable.
      </p>

      <h2>Article 8 — Confidentialité</h2>
      <p>
        Sellerie Académie s'engage à respecter la confidentialité des informations communiquées par le stagiaire
        et son employeur ou financeur. Pour la protection des données personnelles, consultez notre
        <Link to={`${prefix}/confidentialite`}> politique de confidentialité</Link>.
      </p>

      <h2>Article 9 — Réclamations et litiges</h2>
      <p>
        Toute réclamation peut être adressée par courrier ou email à
        <a href="mailto:contact@sellerie-academie.fr"> contact@sellerie-academie.fr</a>. À défaut d'accord
        amiable, le stagiaire peut saisir gratuitement le médiateur de la consommation. En cas d'échec de la
        médiation, le différend sera porté devant les tribunaux compétents du ressort d'Aix-en-Provence, sauf
        disposition légale contraire.
      </p>

      <h2>Article 10 — Loi applicable</h2>
      <p>
        Les présentes conditions générales de vente sont régies par la loi française. Toute version traduite est
        fournie à titre informatif ; seule la version française fait foi.
      </p>
    </LegalShell>
  </>
  );
};
